// El buscador dentro de un modal: lo que abre un botón de la landing («Buscar tren + coche»,
// «Buscar escapada»).
//
//   RenfeViajesBuscador.abrirEnModal(hueco)       // un <div> con los data-* de siempre
//   RenfeViajesBuscador.abrirEnModal({ producto: 'tren-coche', marca: '581296', ... })
//
// Cada apertura monta una INSTANCIA NUEVA del widget, y al cerrar se destruye entera. El
// buscador que la landing ya tenga en la página no se toca: tiene su propio estado, sus propias
// capas y sus propios id (ver montar.js), así que los dos conviven y se puede buscar con
// cualquiera de ellos. Destruir al cerrar, y no esconder, es a propósito: la próxima vez el
// cliente abre un buscador limpio, sin fechas ni pasajeros de una búsqueda que no hizo.
//
// La caja NO lleva la clase del ámbito (`rvb-w`): dentro va la raíz de un widget normal, y con
// un ámbito dentro de otro cada regla del buscador casaría dos veces. Sus estilos cuelgan de su
// propia clase (generar-css.js, MODAL_BUSCADOR) y los colores y medidas de la landing le llegan
// como variables, igual que a la raíz del widget.
import { aplicarColores, aplicarMedidas } from './montar.js'

// Los textos de la caja, que no están en la tabla `traducciones`: el título por defecto de cada
// producto y la etiqueta del botón de cerrar. La landing puede poner el título que quiera
// (`data-titulo`).
const TEXTOS = {
  es: { 'tren-hotel': 'Tren + hotel', 'tren-coche': 'Tren + coche', cerrar: 'Cerrar' },
  en: { 'tren-hotel': 'Train + hotel', 'tren-coche': 'Train + car', cerrar: 'Close' },
  fr: { 'tren-hotel': 'Train + hôtel', 'tren-coche': 'Train + voiture', cerrar: 'Fermer' },
}

const CLASE_ABIERTO = 'rvb-modal-buscador-abierto'
let contador = 0
// Uno a la vez: abrir otro cierra el que hubiera.
let abierto = null

/**
 * @param {object} opciones
 * @param {Element} opciones.hueco            dónde se monta el buscador, ya con sus data-*
 * @param {object} opciones.configuracion     la del hueco, ya leída (embed.js)
 * @param {Function} opciones.crear           monta el buscador en el hueco y lo devuelve
 * @returns {{buscador: object|null, cerrar: Function}}
 */
export function abrirModalBuscador ({ hueco, configuracion, crear }) {
  if (abierto) abierto.cerrar()

  const cfg = configuracion || {}
  const textos = TEXTOS[cfg.locale] || TEXTOS.es
  const titulo = cfg.titulo || (cfg.producto && textos[cfg.producto]) || ''
  const idTitulo = 'rvb-modal-buscador-titulo-' + (++contador)

  const fondo = document.createElement('div')
  fondo.className = 'rvb-modal-buscador-fondo'
  const caja = document.createElement('div')
  caja.className = 'rvb-modal-buscador'
  caja.setAttribute('role', 'dialog')
  caja.setAttribute('aria-modal', 'true')
  caja.setAttribute('tabindex', '-1')

  const cabecera = document.createElement('div')
  cabecera.className = 'rvb-modal-buscador-cabecera'
  if (titulo) {
    const h = document.createElement('h2')
    h.className = 'rvb-modal-buscador-titulo'
    h.id = idTitulo
    h.textContent = titulo
    cabecera.appendChild(h)
    caja.setAttribute('aria-labelledby', idTitulo)
  }
  const cerrarBoton = document.createElement('button')
  cerrarBoton.type = 'button'
  cerrarBoton.className = 'rvb-modal-buscador-cerrar'
  cerrarBoton.setAttribute('aria-label', textos.cerrar)
  cerrarBoton.textContent = '×'
  cabecera.appendChild(cerrarBoton)

  caja.appendChild(cabecera)
  caja.appendChild(hueco)
  fondo.appendChild(caja)

  // Los colores y las medidas de la landing, también en la caja: si no, el título y la cruz
  // saldrían con los de renfeviajes mientras el buscador va con los de la landing.
  aplicarColores([fondo], cfg.colores)
  aplicarMedidas([fondo], cfg.medidas)
  if (cfg.maqueta) fondo.setAttribute('data-maqueta', cfg.maqueta)

  const anterior = document.activeElement

  document.body.appendChild(fondo)

  // LA PÁGINA, ARRIBA ANTES DE BLOQUEARLA. En iPhone, bloquear el documento con la página
  // desplazada hace que iOS lo recorte y SALTE el scroll; el toque que estaba en marcha cae
  // entonces en el sitio equivocado y el navegador lo da por consumido: el primer toque no
  // llega. Es exactamente lo que le pasaba al calendario (ver calendario.js), y el modal se
  // abre desde botones que están a media página, que es justo el caso malo.
  //
  // Subiendo antes, el reajuste ocurre al abrir --cuando nadie está tocando-- y lo de dentro
  // recibe los toques con todo quieto. Al cerrar, la página vuelve donde estaba.
  //
  // No se reproduce en ningún emulador: ni Chrome con un iPhone emulado ni WebKit hacen saltar
  // el scroll. Hace falta el iOS de verdad, con su barra de navegación.
  const scrollAlAbrir = window.pageYOffset || document.documentElement.scrollTop || 0
  window.scrollTo(0, 0)
  document.documentElement.classList.add(CLASE_ABIERTO)

  // El buscador se monta con la caja YA en el documento: el calendario y el panel de móvil
  // miden dónde están al abrirse, y fuera del documento todo mide cero.
  let buscador = null
  try {
    buscador = crear()
  } catch (e) {
    console.error('[buscador renfeviajes] no he podido montar el buscador del modal:', e)
  }

  /**
   * ¿Hay algo del widget abierto encima (el «¿quieres hotel?», los pasajeros, las noches)?
   * Entonces Escape es suyo: cierra eso y no el buscador entero.
   */
  function hayAlgoEncima () {
    return Boolean(buscador && buscador.overlays
      && buscador.overlays.querySelector('.rvb-swal-buscador-renfe-fondo'))
  }

  /**
   * Lo que se puede enfocar dentro del modal: la caja Y las capas del buscador, que cuelgan
   * del `<body>` y no de ella (la lista de estaciones, el calendario). Sin incluirlas, el foco
   * saltaría del último campo a la landing de detrás justo con el desplegable abierto.
   */
  function enfocables () {
    const sitios = [caja, buscador && buscador.overlays].filter(Boolean)
    const todos = []
    for (const sitio of sitios) {
      todos.push(...sitio.querySelectorAll('a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])'))
    }
    return todos.filter((e) => !e.disabled && e.offsetParent !== null)
  }

  function alPulsar (e) {
    // Mientras hay algo del widget encima (el «¿quieres hotel?», los pasajeros, las noches),
    // el teclado es suyo: ese modal trae su propia trampa de foco y su propio Escape.
    if (hayAlgoEncima()) return
    if (e.key === 'Escape') {
      e.preventDefault()
      cerrar()
      return
    }
    if (e.key !== 'Tab') return
    // El foco no se sale del modal: lleva `aria-modal`, así que lo de detrás no existe para
    // quien va con teclado.
    const lista = enfocables()
    if (!lista.length) return
    const i = lista.indexOf(document.activeElement)
    if (e.shiftKey && i <= 0) { e.preventDefault(); lista[lista.length - 1].focus() }
    else if (!e.shiftKey && i === lista.length - 1) { e.preventDefault(); lista[0].focus() }
  }

  function alPresionarFondo (e) {
    // Sólo el velo: lo que pasa dentro de la caja no cierra nada.
    if (e.target === fondo) cerrar()
  }

  let cerrado = false
  function cerrar () {
    if (cerrado) return
    cerrado = true
    document.removeEventListener('keydown', alPulsar)
    if (buscador) { try { buscador.destruir() } catch (e) { /* da igual */ } }
    fondo.remove()
    document.documentElement.classList.remove(CLASE_ABIERTO)
    // La página vuelve donde estaba: subirla es el precio de bloquearla sin saltos, pero dejar
    // al cliente en la cabecera después de cerrar el buscador no lo es. Se restaura ANTES de
    // devolver el foco, porque enfocar el botón con la página arriba la desplazaría otra vez.
    if (scrollAlAbrir) window.scrollTo(0, scrollAlAbrir)
    if (abierto && abierto.cerrar === cerrar) abierto = null
    if (anterior && typeof anterior.focus === 'function') anterior.focus({ preventScroll: true })
  }

  cerrarBoton.addEventListener('click', (e) => { e.preventDefault(); cerrar() })
  fondo.addEventListener('mousedown', alPresionarFondo)
  document.addEventListener('keydown', alPulsar)

  // El foco va a la caja y no al primer campo: en móvil, enfocar una estación abre el panel a
  // pantalla completa, y el cliente todavía no ha tocado nada.
  caja.focus({ preventScroll: true })

  abierto = { buscador, cerrar }
  return abierto
}
